import type jsPDF from 'jspdf';
import type { FailoverFollowThroughReport, FailoverFollowThroughRow, FailoverLegOutcome } from '@/types';
import { formatFailoverOutcomeLabel } from '@/lib/failoverCorrelation';
import { BrandPdf, PDF_COLORS, type Rgb } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import { formatDay, formatGenerated } from './callCostReport';
import { csvText } from './extensionReports';
import { formatDuration } from './queueMissedReport';

/** The Failover Follow-through report's filtering, CSV and PDF, for the Reports page and scheduled reports. */

export const FAILOVER_OUTCOME_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All failover outcomes' },
  { value: 'answered', label: 'Failover answered' },
  { value: 'no_answer', label: 'Rang, no answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'no_leg', label: 'No CDR leg' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'not_applicable', label: 'N/A' },
];

/** Rows with the chosen failover outcome and matching the search. */
export function filterFailoverRows(rows: FailoverFollowThroughRow[], outcome: string, search?: string) {
  const q = (search ?? '').trim().toLowerCase();
  return rows.filter((r) => {
    if (outcome !== 'all' && r.failover_outcome !== outcome) return false;
    if (!q) return true;
    return (
      r.call_from.toLowerCase().includes(q) ||
      r.queue_name.toLowerCase().includes(q) ||
      r.failover_label.toLowerCase().includes(q) ||
      r.what_happened.toLowerCase().includes(q) ||
      r.agents_who_missed.some((a) => a.toLowerCase().includes(q))
    );
  });
}

export function failoverCsv(rows: FailoverFollowThroughRow[]) {
  return csvText(
    [
      'Time',
      'Caller',
      'Queue',
      'Queue status',
      'Wait (s)',
      'Timed out',
      'Agents who missed',
      'Failover destination',
      'Failover outcome',
      'Failover ring (s)',
      'Failover talk (s)',
      'Busy during',
      'What happened',
    ],
    rows.map((r) => [
      r.time,
      r.call_from,
      r.queue_name,
      r.queue_status,
      r.wait_seconds,
      r.timed_out ? 'yes' : 'no',
      r.agents_who_missed.join('; '),
      r.failover_label,
      formatFailoverOutcomeLabel(r.failover_outcome),
      r.failover_ring_seconds ?? '',
      r.failover_talk_seconds ?? '',
      r.busy_during_failover ? 'yes' : 'no',
      r.what_happened,
    ])
  );
}

const c = PDF_COLORS;

const outcomeColors: Record<FailoverLegOutcome, Rgb> = {
  answered: c.green,
  no_answer: c.red,
  busy: c.red,
  no_leg: c.red,
  voicemail: c.amber,
  not_applicable: c.muted,
  unknown: c.muted,
};

export function failoverPdf(input: {
  report: FailoverFollowThroughReport;
  rows: FailoverFollowThroughRow[];
  filtersText: string[];
  startDate: string;
  endDate: string;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
}): jsPDF {
  const { rows } = input;
  const s = input.report.summary;
  const doc = new BrandPdf({
    orientation: 'landscape',
    assets: input.assets,
    reportName: 'Failover follow-through',
    title: 'Queue misses and their failover',
    subtitle: `${formatDay(input.startDate)} – ${formatDay(input.endDate)}  ·  ${input.filtersText.join('  ·  ')}`,
    customerName: input.customerName,
    generated: `Generated ${formatGenerated(input.now ?? new Date())}`,
  });
  doc.statTiles([
    { label: 'Queue misses', value: String(s.totalQueueMisses), caption: `${s.timedOut} reached max wait`, featured: true },
    { label: 'Failover answered', value: String(s.failoverAnswered), dot: c.green, caption: 'Picked up after the queue' },
    { label: 'Rang, no answer', value: String(s.failoverNoAnswer), dot: c.red, caption: 'Failover rang out' },
    { label: 'No CDR leg found', value: String(s.failoverNoLeg), dot: c.red, caption: 'Never reached the failover' },
    { label: 'Busy during failover', value: String(s.busyDuringCount), dot: c.orange, caption: 'Failover was on a call' },
  ]);
  doc.table({
    title: 'Queue misses',
    meta: `${rows.length} call${rows.length === 1 ? '' : 's'}`,
    columns: [
      { header: 'Time', width: 30 },
      { header: 'Caller', width: 30 },
      { header: 'Queue', width: 26 },
      { header: 'Wait', width: 14, align: 'right' },
      { header: 'Agents who missed', width: 34, wrap: true },
      { header: 'Failover to', width: 30 },
      { header: 'Failover outcome', width: 30 },
      { header: 'What happened', width: 86, wrap: true },
    ],
    rows: rows.map((row) => [
      row.time,
      row.call_from || '—',
      row.queue_name,
      `${formatDuration(row.wait_seconds)}${row.timed_out ? '*' : ''}`,
      row.agents_who_missed.join(', ') || '—',
      row.failover_label || '—',
      { text: formatFailoverOutcomeLabel(row.failover_outcome), color: outcomeColors[row.failover_outcome], badge: true },
      row.what_happened,
    ]),
  });
  if (rows.some((row) => row.timed_out)) doc.note('* Reached the queue’s max wait time.');
  return doc.finish();
}
